import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import MangaCard from "@/components/feature/MangaCard";
import { mangas, siteInfo } from "@/mocks/manga";

export default function MangaDetail() {
  const { mangaId } = useParams<{ mangaId: string }>();
  const navigate = useNavigate();
  const [readingMode, setReadingMode] = useState(false);

  const manga = useMemo(
    () => mangas.find((m) => m.id === Number(mangaId)),
    [mangaId]
  );

  useEffect(() => {
    if (manga) {
      document.title = `${manga.title} - 九妖漫画网 | 免费下拉式漫画全集在线阅读`;
    }
  }, [manga]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [mangaId]);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block" />
            <p className="text-foreground-600 mb-4">漫画未找到</p>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMangas = mangas
    .filter(
      (m) =>
        m.id !== manga.id &&
        (m.category === manga.category || m.subcategory === manga.subcategory)
    )
    .slice(0, 4);

  const breadcrumbItems = [
    { name: "首页", path: "/" },
    { name: manga.category === "guoman" ? "国漫推荐" : manga.category === "riman" ? "日漫精选" : manga.category === "hanman" ? "韩漫热门" : "全部分类", path: `/category/${manga.category}` },
    { name: manga.title },
  ];

  const chapterList = Array.from({ length: Math.min(manga.chapters, 50) }, (_, i) => ({
    num: manga.chapters - i,
    title: `第${manga.chapters - i}话`,
    date: new Date(Date.now() - i * 86400000).toISOString().split("T")[0],
  }));

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${manga.title} - 免费下拉式漫画全集在线阅读`,
    description: manga.description,
    author: { "@type": "Person", name: manga.author },
    datePublished: "2024-01-15",
    publisher: { "@type": "Organization", name: siteInfo.name },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          {/* Manga Info */}
          <section className="px-4 md:px-6 py-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 免费下拉式漫画`}
                    className="w-full object-cover object-top"
                    style={{ aspectRatio: "3/4" }}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {manga.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-600">作者：{manga.author}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    manga.status === "连载中" ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-700"
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500" />
                    <span className="text-sm font-semibold text-foreground-900">{manga.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-line text-foreground-400" />
                    <span className="text-sm text-foreground-600">{manga.views}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-file-list-line text-foreground-400" />
                    <span className="text-sm text-foreground-600">共{manga.chapters}话</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {manga.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${manga.subcategory}`}
                      className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mt-5 bg-background-100 rounded-lg p-4 border border-background-200/70">
                  {manga.description}
                </p>

                <div className="flex flex-wrap gap-3 mt-6">
                  <button
                    onClick={() => setReadingMode(true)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-open-line" /> 开始阅读（第1话）
                  </button>
                  <button
                    onClick={() => setReadingMode(true)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full font-medium hover:bg-background-200 border border-background-200/70 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-skip-forward-line" /> 阅读最新（{manga.updateChapter}）
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Reading Mode */}
          {readingMode && (
            <section className="px-4 md:px-6 py-6">
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900">
                    下拉式阅读区域
                  </h3>
                  <button
                    onClick={() => setReadingMode(false)}
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-close-line mr-1" /> 收起
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20manga%20comic%20page%20in%20black%20and%20white%20art%20style%20with%20dynamic%20action%20scenes%20and%20speech%20bubbles%2C%20professional%20manga%20illustration%2C%20clean%20linework%2C%20dramatic%20composition&width=1200&height=1600&seq=manga-reading-01&orientation=portrait"
                      alt="漫画阅读页面"
                      title="免费下拉式漫画在线阅读"
                      className="w-full"
                    />
                  </div>
                  <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20intense%20battle%20scene%20and%20Japanese%20sound%20effects%2C%20detailed%20linework%2C%20professional%20manga%20illustration%2C%20clean%20composition&width=1200&height=1600&seq=manga-reading-02&orientation=portrait"
                      alt="漫画阅读页面"
                      title="免费下拉式漫画在线阅读"
                      className="w-full"
                    />
                  </div>
                  <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20emotional%20character%20closeup%20and%20dialogue%20bubbles%2C%20expressive%20art%20style%2C%20professional%20manga%20illustration%2C%20clean%20composition&width=1200&height=1600&seq=manga-reading-03&orientation=portrait"
                      alt="漫画阅读页面"
                      title="免费下拉式漫画在线阅读"
                      className="w-full"
                    />
                  </div>
                  <div className="text-center py-4">
                    <p className="text-sm text-foreground-500">继续向下滚动加载更多漫画内容...</p>
                    <button className="mt-3 inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-down-line" /> 加载下一话
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Chapter List */}
          <section className="px-4 md:px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-bold text-foreground-900">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-xs px-3 py-1.5 rounded-md bg-primary-500 text-white whitespace-nowrap cursor-pointer">正序</button>
                <button className="text-xs px-3 py-1.5 rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer">倒序</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {chapterList.map((ch) => (
                <button
                  key={ch.num}
                  onClick={() => setReadingMode(true)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 border border-background-200/70 cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-200">
                    {ch.title}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Related Manga */}
          {relatedMangas.length > 0 && (
            <section className="px-4 md:px-6 py-6 md:py-10">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedMangas.map((m) => (
                  <MangaCard key={m.id} {...m} tags={m.tags} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}